import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Team } from '@/types/account';
import { accountService } from '@/services/accountService';

interface TeamState {
  currentTeam: Team | null;
  myTeams: Team[];
  isLoading: boolean;
  setCurrentTeam: (team: Team | null) => void;
  setMyTeams: (teams: Team[]) => void;
  clearTeam: () => void;
  clearMyTeams: () => void;
  fetchMyTeam: () => Promise<void>;
  /** 输入团队密码加入团队；成功后把该团队写入 myTeams 并设为当前团队 */
  joinTeam: (teamId: number, password: string) => Promise<boolean>;
  leaveTeam: (teamId: number) => Promise<boolean>;
}

/**
 * 「最早加入的团队」排在第一位。
 * 后端 /api/account/teams/my_team/ 已按 joined_time 升序返回，这里再做一次
 * 兜底排序：joined_time 缺失的（老接口/本地缓存）用 id 升序垫底，保证顺序稳定。
 */
const sortByJoinedTime = (teams: Team[]): Team[] =>
  [...teams].sort((a, b) => {
    const ta = a.joined_time || '';
    const tb = b.joined_time || '';
    if (ta && tb && ta !== tb) return ta < tb ? -1 : 1;
    if (ta && !tb) return -1;
    if (!ta && tb) return 1;
    return a.id - b.id;
  });

export const useTeamStore = create<TeamState>()(
  persist(
    (set, get) => ({
      currentTeam: null,
      myTeams: [],
      isLoading: false,
      setCurrentTeam: (team: Team | null) => set({ currentTeam: team }),
      setMyTeams: (teams: Team[]) => set({ myTeams: sortByJoinedTime(teams) }),
      clearTeam: () => set({ currentTeam: null }),
      clearMyTeams: () => set({ myTeams: [] }),

      // 获取用户所属的团队（含自建团队，创建者已自动成为成员）
      fetchMyTeam: async () => {
        set({ isLoading: true });
        try {
          const response = await accountService.getMyTeam();
          if (response.data && Array.isArray(response.data)) {
            const teams = sortByJoinedTime(response.data);
            set({ myTeams: teams });
            // 默认团队：最早加入的第一个团队；退出/未加入时为 null
            set({ currentTeam: teams.length > 0 ? teams[0] : null });
          } else {
            set({ myTeams: [], currentTeam: null });
          }
        } catch (error) {
          console.error('获取用户团队失败:', error);
          set({ myTeams: [], currentTeam: null });
        } finally {
          set({ isLoading: false });
        }
      },

      // 密码加入团队（训练页「未加入的团队」入口）
      joinTeam: async (teamId: number, password: string) => {
        try {
          await accountService.joinTeam(teamId, { team_id: teamId, password });
          // 重新拉取，保证 joined_time / member_count 与后端一致
          const response = await accountService.getMyTeam();
          const teams = Array.isArray(response.data) ? sortByJoinedTime(response.data) : [];
          set({ myTeams: teams });
          const joined = teams.find((t) => t.id === teamId) || null;
          if (joined) set({ currentTeam: joined });
          return true;
        } catch (error) {
          console.error('加入团队失败:', error);
          return false;
        }
      },

      // 退出团队
      leaveTeam: async (teamId: number) => {
        try {
          await accountService.leaveTeam(teamId);
          // 从团队列表中移除该团队
          const { myTeams, currentTeam } = get();
          const updatedTeams = myTeams.filter(team => team.id !== teamId);
          set({ myTeams: updatedTeams });

          // 如果退出的是当前选中的团队，选择其他团队或清空
          if (currentTeam && currentTeam.id === teamId) {
            if (updatedTeams.length > 0) {
              set({ currentTeam: updatedTeams[0] });
            } else {
              set({ currentTeam: null });
            }
          }
          return true;
        } catch (error) {
          console.error('退出团队失败:', error);
          return false;
        }
      },
    }),
    {
      name: 'team-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currentTeam: state.currentTeam,
        myTeams: state.myTeams,
      }),
    }
  )
);
